import type { Condition } from "@/types";

import {
  QueryContentMode,
  type QueryTarget,
  type SubmitStructuredQueryRequest,
} from "./QueryRecord";
import type { QueryService } from "./QueryService";

/** The slice of {@link QueryService} an id search uses. */
export type ResourceIdQueryService = Pick<
  QueryService,
  "submitStructured" | "getQueryResults"
>;

/** One id search over a unified query target. */
export interface ResourceIdSearch {
  /** The org whose resources are searched. */
  orgId: string;
  target: QueryTarget;
  /**
   * The id column, both the condition's field and the key each result record
   * carries the id under (`device_id`, `dataset_id`, ...).
   */
  idField: string;
  /**
   * How a typed term matches. `contains` is a case-insensitive substring match
   * (SQL `ILIKE`); `equals` is for id columns whose catalog entry admits only
   * `EQUALS` / `NOT_EQUALS` (session and collection ids), where a typed term
   * can only find the one id it spells out exactly.
   */
  match: "contains" | "equals";
  /** A sortable field of the target; keeps the returned page deterministic. */
  sortBy: string;
  sortDirection: "ASC" | "DESC";
  /** The typed term, used as given. Empty lists the target's first page unfiltered. */
  search: string;
  /** The page size. */
  limit: number;
  signal?: AbortSignal;
}

/** The ids a search found, and whether more matched than were returned. */
export interface ResourceIdPage {
  ids: string[];
  truncated: boolean;
}

/**
 * Search one query target's ids through the unified query endpoints: submit a
 * structured query on the id column, then read its first page.
 */
export async function searchResourceIds(
  queryService: ResourceIdQueryService,
  {
    orgId,
    target,
    idField,
    match,
    sortBy,
    sortDirection,
    search,
    limit,
    signal,
  }: ResourceIdSearch,
): Promise<ResourceIdPage> {
  const condition: Condition | undefined =
    search === ""
      ? undefined
      : {
          field: idField,
          comparator: match === "contains" ? "CONTAINS" : "EQUALS",
          value: search,
        };
  const request: SubmitStructuredQueryRequest = {
    content_mode: QueryContentMode.RecordWithMeta,
    target,
    query: {
      ...(condition && { condition }),
      sort_by: sortBy,
      sort_direction: sortDirection,
      limit,
    },
  };
  const record = await queryService.submitStructured(request, {
    resourceOwnerId: orgId,
    abortSignal: signal,
  });
  const page = await queryService.getQueryResults<Record<string, unknown>>(
    record.query_id,
    { pageSize: limit, abortSignal: signal },
  );
  return {
    ids: (page.items ?? [])
      .map((item) => item[idField])
      .filter((id): id is string => typeof id === "string"),
    // A continuation token means the server had more matches than it returned.
    truncated: Boolean(page.next_token),
  };
}
